import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Plus, Trash2, Pencil, Save, X, Wrench } from "lucide-react";

const fmt = (n, d = 2) => (n ?? 0).toLocaleString("en-ZA", { minimumFractionDigits: d, maximumFractionDigits: d });

const blank = () => ({
  id: null, name: "", cost: 0, useful_life_years: 5,
  purchase_date: "", notes: "", is_active: true,
});

export default function Equipment() {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [editing, setEditing] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true); setError("");
    try {
      const [a, b] = await Promise.all([
        api.get("/equipment"),
        api.get("/equipment/summary"),
      ]);
      setItems(a.data); setSummary(b.data);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault(); setError("");
    try {
      const payload = {
        name: editing.name,
        cost: parseFloat(editing.cost) || 0,
        useful_life_years: parseFloat(editing.useful_life_years) || 1,
        purchase_date: editing.purchase_date || null,
        notes: editing.notes || "",
        is_active: !!editing.is_active,
      };
      if (editing.id) {
        await api.put(`/equipment/${editing.id}`, payload);
      } else {
        await api.post("/equipment", payload);
      }
      setEditing(null);
      await load();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this equipment item?")) return;
    await api.delete(`/equipment/${id}`);
    await load();
  };

  return (
    <div data-testid="equipment-page">
      <PageHeader
        eyebrow="Costing"
        title="Manufacturing Labour & Equipment"
        description="Capital assets used in production + monthly manufacturing labour. Annual depreciation (cost ÷ useful life) is divided across last year's kg sold to give a Rand-per-kg equipment cost flowing into the Manufacturing Cost calculation."
        actions={
          <button onClick={() => setEditing(blank())} data-testid="equipment-new"
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded text-sm font-semibold hover:bg-[hsl(20,41%,32%)]">
            <Plus className="w-4 h-4" /> New equipment
          </button>
        }
      />

      {error && <div className="bg-destructive/10 border border-destructive/30 rounded p-3 text-sm mb-4">{error}</div>}

      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6" data-testid="equipment-summary">
          <Stat label="Annual amortisation" value={`R ${fmt(summary.annual_amortisation, 2)}`} hint={`${(summary.items || []).length} active items`} />
          <Stat label={`Kg sold in FY ${summary.fy_year}`} value={`${fmt(summary.kg_sold_in_fy, 1)} kg`} hint={summary.kg_data_complete ? "complete" : "warming…"} />
          <Stat label="Equipment cost / kg sold" value={`R ${fmt(summary.rand_per_kg, 4)}`} accent="ok" hint="Flows into Mfg Cost tab" />
        </div>
      )}

      <div className="bg-card border border-border rounded overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-secondary">
            <tr>
              <th className="text-left p-3 label-tiny">Name</th>
              <th className="text-right p-3 label-tiny">Cost</th>
              <th className="text-right p-3 label-tiny">Life (yrs)</th>
              <th className="text-right p-3 label-tiny">Annual</th>
              <th className="text-left p-3 label-tiny">Purchased</th>
              <th className="text-left p-3 label-tiny">Status</th>
              <th className="text-right p-3 label-tiny">Action</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan="7" className="p-8 text-center text-muted-foreground">
                {busy ? "Loading…" : "No equipment yet — click \"New equipment\" to add your first asset."}
              </td></tr>
            )}
            {items.map((it) => (
              <tr key={it.id} className="border-t border-border" data-testid={`equipment-row-${it.id}`}>
                <td className="p-3 font-medium flex items-center gap-2">
                  <Wrench className="w-3.5 h-3.5 text-muted-foreground" />
                  {it.name}
                </td>
                <td className="p-3 text-right font-mono">R {fmt(it.cost)}</td>
                <td className="p-3 text-right font-mono">{it.useful_life_years}</td>
                <td className="p-3 text-right font-mono text-primary font-semibold">R {fmt(it.cost / it.useful_life_years)}</td>
                <td className="p-3 font-mono text-xs">{it.purchase_date || "—"}</td>
                <td className="p-3">
                  {it.is_active
                    ? <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-[hsl(132,18%,36%)]/10 text-[hsl(132,18%,36%)] font-bold">active</span>
                    : <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-muted text-muted-foreground font-bold">retired</span>}
                </td>
                <td className="p-3 text-right">
                  <div className="inline-flex gap-1">
                    <button onClick={() => setEditing({ ...it })} data-testid={`equipment-edit-${it.id}`}
                      className="p-1.5 text-muted-foreground border border-border rounded hover:bg-secondary">
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => remove(it.id)} data-testid={`equipment-delete-${it.id}`}
                      className="p-1.5 text-destructive border border-destructive/30 rounded hover:bg-destructive/10">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && <EditModal item={editing} setItem={setEditing} onClose={() => setEditing(null)} onSave={save} />}

      <ManufacturingLabourSection />
    </div>
  );
}

function ManufacturingLabourSection() {
  const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const today = new Date();
  const [list, setList] = useState([]);
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [rate, setRate] = useState(0);
  const [hours, setHours] = useState(0);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [error, setError] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/monthly-costs");
      setList(data);
      const m = data.find((x) => x.year === year && x.month === month);
      setRate(m?.labour_rate_per_hour ?? 0);
      setHours(m?.labour_hours ?? 0);
    } catch (e) { setError(formatApiError(e.response?.data?.detail) || e.message); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [year, month]);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true); setMsg(""); setError("");
    try {
      await api.patch("/monthly-costs/labour", {
        year, month,
        labour_rate_per_hour: parseFloat(rate) || 0,
        labour_hours: parseFloat(hours) || 0,
      });
      setMsg("Saved");
      await load();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  const total = (parseFloat(rate) || 0) * (parseFloat(hours) || 0);

  return (
    <div className="bg-card border border-border rounded p-5 mt-8" data-testid="labour-section">
      <h3 className="font-display text-lg font-bold mb-1">Manufacturing Labour</h3>
      <p className="text-xs text-muted-foreground mb-4">
        Manual monthly entry. Labour cost = rate per hour × total hours. Flows into the Manufacturing Cost tab as labour R/kg.
      </p>

      <form onSubmit={save} className="grid grid-cols-2 md:grid-cols-5 gap-3 items-end mb-4">
        <div>
          <label className="label-tiny mb-1.5 block">Year</label>
          <select value={year} onChange={(e) => setYear(parseInt(e.target.value))} data-testid="labour-year"
            className="w-full px-3 py-2 bg-white border border-border rounded text-sm">
            {[2024, 2025, 2026, 2027].map((y) => <option key={y}>{y}</option>)}
          </select>
        </div>
        <div>
          <label className="label-tiny mb-1.5 block">Month</label>
          <select value={month} onChange={(e) => setMonth(parseInt(e.target.value))} data-testid="labour-month"
            className="w-full px-3 py-2 bg-white border border-border rounded text-sm">
            {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
          </select>
        </div>
        <div>
          <label className="label-tiny mb-1.5 block">Rate per hour (R)</label>
          <input type="number" step="0.01" min="0" value={rate} onChange={(e) => setRate(e.target.value)}
            data-testid="labour-rate" className="w-full px-3 py-2 bg-white border border-border rounded font-mono text-sm" />
        </div>
        <div>
          <label className="label-tiny mb-1.5 block">Total hours</label>
          <input type="number" step="0.5" min="0" value={hours} onChange={(e) => setHours(e.target.value)}
            data-testid="labour-hours" className="w-full px-3 py-2 bg-white border border-border rounded font-mono text-sm" />
        </div>
        <div>
          <button type="submit" disabled={busy} data-testid="labour-save"
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded text-sm font-semibold hover:bg-[hsl(20,41%,32%)] disabled:opacity-60">
            <Save className="w-4 h-4" /> {busy ? "Saving…" : "Save labour"}
          </button>
        </div>
      </form>

      <div className="flex items-center justify-between gap-3 bg-secondary border border-border rounded px-4 py-2 mb-4">
        <span className="label-tiny">Computed labour cost for {MONTHS[month - 1]} {year}</span>
        <span className="font-display text-lg font-bold tabular-nums" data-testid="labour-total">R {fmt(total)}</span>
      </div>

      {msg && <p className="text-sm text-[hsl(132,18%,36%)] mb-3">{msg}</p>}
      {error && <p className="text-sm text-destructive mb-3">{error}</p>}

      <div className="border border-border rounded overflow-hidden">
        <table className="w-full text-sm" data-testid="labour-history">
          <thead className="bg-secondary">
            <tr>
              <th className="label-tiny px-3 py-2 text-left">Period</th>
              <th className="label-tiny px-3 py-2 text-right">Rate / hr</th>
              <th className="label-tiny px-3 py-2 text-right">Hours</th>
              <th className="label-tiny px-3 py-2 text-right">Total labour</th>
              <th className="label-tiny px-3 py-2 text-right w-32">Actions</th>
            </tr>
          </thead>
          <tbody>
            {list.filter((m) => (m.labour_rate_per_hour || 0) > 0 || (m.labour_hours || 0) > 0).length === 0 && (
              <tr><td colSpan={5} className="px-3 py-6 text-center text-muted-foreground text-xs">
                No labour entries yet.
              </td></tr>
            )}
            {list.filter((m) => (m.labour_rate_per_hour || 0) > 0 || (m.labour_hours || 0) > 0).map((m) => {
              const t = (m.labour_rate_per_hour || 0) * (m.labour_hours || 0);
              const isCurrent = m.year === year && m.month === month;
              return (
                <tr key={`${m.year}-${m.month}`} className="border-t border-border hover:bg-secondary/40"
                  data-testid={`labour-row-${m.year}-${m.month}`}>
                  <td className="px-3 py-2">{MONTHS[m.month - 1]} {m.year}</td>
                  <td className="px-3 py-2 text-right tabular-nums">R {fmt(m.labour_rate_per_hour || 0)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{fmt(m.labour_hours || 0, 1)}</td>
                  <td className="px-3 py-2 text-right tabular-nums font-semibold">R {fmt(t)}</td>
                  <td className="px-3 py-2 text-right">
                    <div className="inline-flex gap-1">
                      <button type="button" onClick={() => { setYear(m.year); setMonth(m.month); }}
                        data-testid={`labour-edit-${m.year}-${m.month}`}
                        className={`px-2 py-1 text-xs border rounded hover:bg-secondary ${isCurrent ? "border-primary text-primary" : "border-border text-muted-foreground"}`}>
                        Edit
                      </button>
                      <button type="button"
                        onClick={async () => {
                          if (!window.confirm(`Clear labour for ${MONTHS[m.month - 1]} ${m.year}?`)) return;
                          try {
                            await api.delete(`/monthly-costs/labour/${m.year}/${m.month}`);
                            await load();
                          } catch (err) {
                            setError(formatApiError(err.response?.data?.detail) || err.message);
                          }
                        }}
                        data-testid={`labour-delete-${m.year}-${m.month}`}
                        className="px-2 py-1 text-xs text-destructive border border-destructive/30 rounded hover:bg-destructive/10">
                        Clear
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Stat({ label, value, hint, accent }) {
  const tone = accent === "ok" ? "text-[hsl(132,18%,36%)]" : "text-foreground";
  return (
    <div className="bg-card border border-border rounded p-4">
      <div className="label-tiny text-muted-foreground mb-1">{label}</div>
      <div className={`font-display text-xl font-bold ${tone}`}>{value}</div>
      {hint && <div className="text-[10px] text-muted-foreground mt-1 font-mono">{hint}</div>}
    </div>
  );
}

function EditModal({ item, setItem, onClose, onSave }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/50 p-4" data-testid="equipment-edit-modal">
      <div className="bg-card border border-border rounded shadow-xl max-w-md w-full">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <h3 className="font-display text-lg font-bold">{item.id ? "Edit" : "New"} equipment</h3>
          <button onClick={onClose} className="p-1 text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>
        </div>
        <form onSubmit={onSave} className="p-5 space-y-3 text-sm">
          <Field label="Name" required>
            <input type="text" required value={item.name} data-testid="equipment-field-name"
              onChange={(e) => setItem({ ...item, name: e.target.value })}
              className="w-full px-3 py-2 border border-border rounded" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Cost (ZAR)" required>
              <input type="number" step="0.01" min="0" required value={item.cost} data-testid="equipment-field-cost"
                onChange={(e) => setItem({ ...item, cost: e.target.value })}
                className="w-full px-3 py-2 border border-border rounded font-mono" />
            </Field>
            <Field label="Useful life (years)" required>
              <input type="number" step="0.5" min="0.5" required value={item.useful_life_years} data-testid="equipment-field-life"
                onChange={(e) => setItem({ ...item, useful_life_years: e.target.value })}
                className="w-full px-3 py-2 border border-border rounded font-mono" />
            </Field>
          </div>
          <Field label="Purchase date">
            <input type="date" value={item.purchase_date || ""} data-testid="equipment-field-date"
              onChange={(e) => setItem({ ...item, purchase_date: e.target.value })}
              className="w-full px-3 py-2 border border-border rounded" />
          </Field>
          <Field label="Notes">
            <textarea value={item.notes || ""} data-testid="equipment-field-notes"
              onChange={(e) => setItem({ ...item, notes: e.target.value })}
              rows="2" className="w-full px-3 py-2 border border-border rounded text-xs" />
          </Field>
          <label className="inline-flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={!!item.is_active}
              onChange={(e) => setItem({ ...item, is_active: e.target.checked })}
              className="accent-[hsl(20,39%,39%)]" />
            <span className="text-sm">Active (include in amortisation)</span>
          </label>
          <div className="flex gap-2 justify-end pt-2 border-t border-border">
            <button type="button" onClick={onClose} className="px-4 py-2 border border-border rounded text-sm hover:bg-secondary">Cancel</button>
            <button type="submit" data-testid="equipment-save"
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded text-sm font-semibold hover:bg-[hsl(20,41%,32%)]">
              <Save className="w-4 h-4" /> Save
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <p className="label-tiny mb-1">{label}{required && <span className="text-destructive ml-0.5">*</span>}</p>
      {children}
    </div>
  );
}
